import { KROT } from 'captcha-eu-sdk';
import { type ChangeEvent, type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import apiService from '@/services/apiService';
import { notifyParent } from '@/services/parentMessenger';
import {
	getValidationMessage,
	validateEmail,
	validatePhone,
	validateRequired
} from '@/services/validators';
import Modal from './Modal';
import Spinner from './Spinner';

interface FormData {
	name: string;
	phone: string;
	email: string;
	message: string;
}

interface Errors {
	name?: string;
	phone?: string;
	email?: string;
}

interface SubmitStatus {
	type: string;
	message: string;
}

interface ModalContactProps {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	/** When set (e.g. funnel “advisor” entry), replaces the generic Neem-contact heading. */
	marketingTitle?: string;
	marketingDescription?: string;
}

const ModalContact = ({
	open: controlledOpen,
	onOpenChange: controlledOnOpenChange,
	marketingTitle,
	marketingDescription
}: ModalContactProps = {}) => {
	const { t } = useTranslation();
	const { appData, applicationData, config } = useAppContext();
	const id = appData?.id;
	const [internalOpen, setInternalOpen] = useState(false);
	// Inside the funnel we usually already know name/phone/email from
	// earlier steps. Prefilling lets the customer click "send" without
	// retyping while still allowing edits if they want to update.
	const buildInitialForm = useCallback(
		(): FormData => ({
			name: [applicationData?.initials, applicationData?.prefix, applicationData?.lastName]
				.filter(Boolean)
				.join(' ')
				.trim(),
			phone: applicationData?.phone || '',
			email: applicationData?.email || '',
			message: ''
		}),
		[applicationData]
	);

	const [formData, setFormData] = useState<FormData>(buildInitialForm);
	const [errors, setErrors] = useState<Errors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({ type: '', message: '' });
	const CAPTCHA_PUBLIC_KEY = import.meta.env.VITE_CAPTCHA_PUBLIC_KEY;

	const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
	const setOpen = controlledOnOpenChange || setInternalOpen;

	useEffect(() => {
		KROT.setup(CAPTCHA_PUBLIC_KEY);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getCaptchaSolution = async () => {
		if (window.localStorage.getItem('__e2e__')) return '';
		const solution = await KROT.getSolution();
		return JSON.stringify(solution);
	};

	const validateForm = () => {
		const newErrors: Errors = {};

		// Frontend requires name even though the backend treats it as optional,
		// so the advisor always has someone to address in the reply.
		const nameError = validateRequired(formData.name);
		if (nameError) {
			newErrors.name = getValidationMessage(nameError);
		}

		// Phone OR email is required (mirrors backend Pydantic check). We
		// intentionally don't require both — many customers prefer to be
		// reached only by phone, and forcing both creates dropoff.
		const hasPhone = !!formData.phone.trim();
		const hasEmail = !!formData.email.trim();

		if (hasPhone) {
			const phoneError = validatePhone(formData.phone);
			if (phoneError) {
				newErrors.phone = getValidationMessage(phoneError);
			}
		}
		if (hasEmail) {
			const emailError = validateEmail(formData.email);
			if (emailError) {
				newErrors.email = getValidationMessage(emailError);
			}
		}
		if (!hasPhone && !hasEmail) {
			const message = getValidationMessage('contact_info_required');
			newErrors.email = message;
			newErrors.phone = message;
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { id, value } = e.target;
		setFormData((prevState) => ({
			...prevState,
			[id]: value
		}));
		if (errors[id as keyof Errors]) {
			setErrors((prev) => ({ ...prev, [id]: '' }));
		}
	};

	const resetForm = useCallback(() => {
		setFormData(buildInitialForm());
		setErrors({});
		setSubmitStatus({ type: '', message: '' });
	}, [buildInitialForm]);

	useEffect(() => {
		if (!open) {
			resetForm();
		}
	}, [open, resetForm]);

	useEffect(() => {
		const handleOpenModal = () => {
			setOpen(true);
		};

		window.addEventListener('openContactModal', handleOpenModal);
		return () => {
			window.removeEventListener('openContactModal', handleOpenModal);
		};
	}, [setOpen]);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);
		setSubmitStatus({ type: '', message: '' });

		try {
			const captchaSolution = await getCaptchaSolution();
			await apiService.contact.submit(
				id || '',
				formData.email,
				formData.phone,
				formData.name,
				captchaSolution,
				formData.message
			);

			// Lead-tracking: the customer submitted the contact / advisor form.
			// No-op when not iframed or no parent origin was captured.
			notifyParent('contact_request');

			setSubmitStatus({
				type: 'success',
				message: 'Bedankt! We nemen zo snel mogelijk contact met je op.'
			});

			setTimeout(() => {
				setOpen(false);
			}, 4000);
		} catch (error) {
			setSubmitStatus({
				type: 'error',
				message: 'Er is iets misgegaan. Probeer het later opnieuw.'
			});
			console.warn(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			id="modalContact"
			open={open}
			onOpenChange={setOpen}
			size="md"
			backdrop={true}
			keyboard={true}
		>
			<div className="py-6 px-4">
				<div className="text-2xl font-bold mb-3">{marketingTitle ?? t('contact.modal.title')}</div>
				<p className="mb-6 text-muted-foreground leading-relaxed">
					<span className="sm:inline-block">
						{marketingDescription ?? t('contact.modal.description')}
					</span>
				</p>

				{submitStatus.message && (
					<Alert
						variant={submitStatus.type === 'success' ? 'success' : 'destructive'}
						className="mb-6"
					>
						<AlertDescription>{submitStatus.message}</AlertDescription>
					</Alert>
				)}

				<form onSubmit={handleSubmit} noValidate className="space-y-4 text-left">
					<Field data-invalid={!!errors.name}>
						<FieldLabel htmlFor="name">{t('contact.modal.nameLabel')}</FieldLabel>
						<Input
							type="text"
							id="name"
							value={formData.name}
							onChange={handleChange}
							disabled={isSubmitting}
							className={`${errors?.name && 'border-destructive'}`}
							data-testid="modal-contact-input-name"
							aria-invalid={!!errors.name}
						/>
						{errors.name && <FieldError>{errors.name}</FieldError>}
					</Field>

					<Field data-invalid={!!errors.phone}>
						<FieldLabel htmlFor="phone">{t('contact.modal.phoneLabel')}</FieldLabel>
						<Input
							type="tel"
							id="phone"
							value={formData.phone}
							onChange={handleChange}
							disabled={isSubmitting}
							className={`${errors?.phone && 'border-destructive'}`}
							data-testid="modal-contact-input-phone"
							aria-invalid={!!errors.phone}
						/>
						{errors.phone && <FieldError>{errors.phone}</FieldError>}
					</Field>

					<Field data-invalid={!!errors.email}>
						<FieldLabel htmlFor="email">{t('contact.modal.emailLabel')}</FieldLabel>
						<Input
							type="email"
							id="email"
							value={formData.email}
							onChange={handleChange}
							disabled={isSubmitting}
							className={`${errors?.email && 'border-destructive'}`}
							data-testid="modal-contact-input-email"
							aria-invalid={!!errors.email}
						/>
						{errors.email && <FieldError>{errors.email}</FieldError>}
					</Field>

					<Field>
						<FieldLabel htmlFor="message">{t('contact.modal.messageLabel')}</FieldLabel>
						<textarea
							id="message"
							value={formData.message}
							onChange={handleChange}
							disabled={isSubmitting}
							rows={3}
							placeholder={t('contact.modal.messagePlaceholder')}
							maxLength={2000}
							className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-neutral-100 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
							data-testid="modal-contact-input-message"
						/>
					</Field>

					<Button
						type="submit"
						className="w-full mt-6"
						size="lg"
						disabled={isSubmitting}
						data-testid="modal-contact-submit-button"
					>
						{isSubmitting ? (
							<Spinner size="sm" variant="current" className="mr-2" visible={isSubmitting} />
						) : (
							t('contact.modal.submitButton')
						)}
					</Button>
				</form>

				{Boolean(config?.email && config?.phone) && (
					<div className="mt-8 text-center">
						<h6 className="text-sm font-bold mb-2">{t('contact.modal.preferSelfContact')}</h6>
						<div className="flex items-center justify-center gap-4 text-sm">
							<a href={`mailto:${config.email}`} className="text-primary hover:underline">
								{String(config.email)}
							</a>
							<a href={`tel:${config.phone}`} className="text-primary hover:underline">
								{String(config.phone)}
							</a>
						</div>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default ModalContact;
